"use client";

// KAN-1016: СТРАХОВКА, а не основное место. Основная настройка zod живёт в
// `instrumentation-client.ts` — он выполняется раньше гидрации и раньше любых
// компонентов. Отсюда одного импорта оказалось мало: `allowsEval`
// мемоизируется при первом обращении, и одна схема успевала разобраться до
// этого модуля — в проде это давало ровно одно оставшееся нарушение CSP из
// тринадцати. Импорт оставлен на случай, если instrumentation-client
// отключат: повторный вызов `config` безвреден.
import "@/shared/lib/zod-config";

import { NextIntlClientProvider, type AbstractIntlMessages } from "next-intl";

import { intlMessageFallback, intlOnError } from "@/i18n/intl-handlers";
import { setUiMessages } from "@/shared/lib/ui-translate";
import { useEffect, useState } from "react";

// intlOnError и intlMessageFallback вынесены в @/i18n/intl-handlers (KAN-975): нужны и слоям сообщений.
// Сырой usePathname из next/navigation, НЕ из @/i18n/navigation (KAN-1188 регресс).
// Хук next-intl читает локаль из NextIntlClientProvider, а тот стоит НИЖЕ по дереву —
// вне провайдера он бросает, и пререндер всех страниц падал («Error occurred
// prerendering page /en/auth/rustlm»). Здесь префикс языка и не нужен: сравниваем
// только с /overlay и /preloader, а они живут в (bare) без префикса.
import { usePathname } from "next/navigation";
import { EntranceGate } from "@/shared/lib/entrance";
import { ErrorBoundary } from "@/shared/lib/error-boundary";
import { AuthProvider } from "@/shared/providers/auth-provider";
import { QueryProvider } from "@/shared/query-provider";
import { ThemeProvider } from "@/shared/theme-provider";
import { HeaderLayoutInit } from "@/shared/ui/header-layout-init";
import { NeoThemeInit } from "@/shared/ui/neo-theme-init";
import { CookieConsent } from "@/shared/ui/cookie-consent";
import { ThirdPartyAnalytics } from "@/shared/ui/third-party-analytics";
import { SkipToContent } from "@/shared/ui/skip-to-content";
import { ClientWrapper } from "@/shared/ui/client-wrapper";
import { CommandPalette } from "@/shared/ui/command-palette.dynamic";
import { ProfilePreviewModalHost } from "@/features/profile-preview/ui/profile-preview-modal-host";
import { RumVitalsReporter } from "@/shared/lib/rum/rum-vitals-reporter";
import { DevPerfTracker } from "@/shared/lib/rum/dev-perf-tracker";
import { AnonPresence } from "@/widgets/stats/anon-presence";
import { TooltipProvider } from "@/shared/ui/overlays/tooltip";
import { Toaster } from "@/shared/ui/sonner";
import { MaintenanceOverlay } from "@/shared/ui/maintenance-overlay";
import { NetworkHintBar } from "@/shared/ui/network-hint-bar";
import { PagePullToRefresh } from "@/shared/ui/pull-to-refresh";
import dynamic from "next/dynamic";
import type { ToolMiniViews } from "@/features/tool-wheel/ui/tool-mini-host";
import { ToolWheelGate } from "@/features/tool-wheel/ui/tool-wheel-gate";
import { TauriTitleBar } from "@/widgets/tauri/tauri-title-bar";
import { UiScaleInit } from "@/entities/user/ui/ui-scale-init";
import { isTauri } from "@/shared/lib/tauri";

const TauriRuntime = dynamic(() => import("@/widgets/tauri/tauri-runtime"), {
  ssr: false,
});
// KAN-1324: портал-виджет чата — отдельным чанком, SSR не нужен.
const ChatWidgetRoot = dynamic(() => import("@/widgets/gl-chat/ui/widget/chat-widget-root"), { ssr: false });
// Колесо тулзов (KAN-867): кнопка и меню клиентские, SSR им не нужен.
const toolMiniViews: ToolMiniViews = {
  compare: dynamic(
    () =>
      import("@/widgets/tools/compare/compare-mini").then((m) => m.CompareMini),
    { ssr: false },
  ),
  tiermaker: dynamic(
    () =>
      import("@/widgets/tools/tiermaker/tiermaker-mini").then(
        (m) => m.TiermakerMini,
      ),
    { ssr: false },
  ),
  scouting: dynamic(
    () =>
      import("@/widgets/tools/scouting/scouting-mini").then(
        (m) => m.ScoutingMini,
      ),
    { ssr: false },
  ),
};

function DisableSmoothThemeTransitions() {
  useEffect(() => {
    document.documentElement.classList.remove("smooth-theme-transitions");
    return () =>
      document.documentElement.classList.remove("smooth-theme-transitions");
  }, []);
  return null;
}

function TauriBodyStyle() {
  useEffect(() => {
    if (!isTauri) return;
    const html = document.documentElement;
    const body = document.body;
    html.setAttribute("data-tauri", "true");
    const bg = "var(--background)";
    html.style.background = bg;
    html.style.minHeight = "100%";
    body.style.background = bg;
    body.style.minHeight = "100vh";
    body.style.margin = "0";
    return () => {
      html.removeAttribute("data-tauri");
      html.style.background = "";
      html.style.minHeight = "";
      body.style.background = "";
      body.style.minHeight = "";
      body.style.margin = "";
    };
  }, []);
  return null;
}

function OverlayShell({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    html.setAttribute("data-overlay", "true");
    html.style.background = "transparent";
    body.style.background = "transparent";
    body.style.backgroundColor = "transparent";
    return () => {
      html.removeAttribute("data-overlay");
      html.style.background = "";
      body.style.background = "";
      body.style.backgroundColor = "";
    };
  }, []);
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "transparent",
        backgroundColor: "transparent",
      }}
    >
      {children}
    </div>
  );
}

function FullAppShell({
  children,
  locale,
  messages,
  timeZone,
  pathname,
}: {
  children: React.ReactNode;
  locale: string;
  messages: Record<string, unknown>;
  timeZone: string;
  pathname: string;
}) {
  const [showTauriShell, setShowTauriShell] = useState(false);
  useEffect(() => {
    if (isTauri) {
      setShowTauriShell(true);
      // Навешиваем класс из эффекта (а не только из inline-скрипта в <head>):
      // window.__TAURI__ может быть ещё не инжектирован на момент раннего скрипта.
      // В Tauri-shell скролл идёт по внутреннему overflow-auto, а scrollbar-gutter
      // на html резервирует пустую полосу справа от ВСЕГО (включая title bar) —
      // .is-tauri в globals.css отключает эту резервацию.
      document.documentElement.classList.add("is-tauri");
    }
  }, []);

  const isPreloader = pathname === "/preloader";
  // Лендинг студии — отдельная витрина: чат и колесо тулзов турнирной платформы там лишние.
  const isStudio = /^\/[a-z]{2}\/studio(\/|$)/.test(pathname);

  return (
    <NextIntlClientProvider
      locale={locale}
      messages={messages}
      timeZone={timeZone}
      onError={intlOnError}
      getMessageFallback={intlMessageFallback}
    >
      <ErrorBoundary>
        <ThemeProvider
          attribute="class"
          defaultTheme="dark"
          enableSystem={false}
          disableTransitionOnChange={false}
        >
          <HeaderLayoutInit />
          <NeoThemeInit />
          <UiScaleInit />
          <DisableSmoothThemeTransitions />
          <EntranceGate />
          <QueryProvider>
            <TooltipProvider>
              <AuthProvider>
                <TauriBodyStyle />
                <div
                  className={
                    showTauriShell
                      ? "flex h-screen flex-col overflow-hidden bg-background"
                      : ""
                  }
                >
                  {showTauriShell && !isPreloader && <TauriTitleBar />}
                  {!isPreloader && <SkipToContent />}
                  <ClientWrapper
                    className={
                      showTauriShell
                        ? "flex-1 min-h-0 overflow-auto"
                        : undefined
                    }
                  >
                    {children}
                  </ClientWrapper>
                </div>
                <Toaster />
                {/* KAN-283: полноэкранный оверлей «сайт обновляется» при недоступности backend */}
                <MaintenanceOverlay />
                <NetworkHintBar />
                <PagePullToRefresh />
                {/* KAN-297: desktop-рантайм отдельным чанком — на вебе не грузится */}
                {showTauriShell && <TauriRuntime />}
                <CommandPalette />
                {!showTauriShell && !isStudio && <ToolWheelGate miniViews={toolMiniViews} />}
                <ProfilePreviewModalHost />
                {!showTauriShell && !isPreloader && !isStudio && <ChatWidgetRoot />}
                <RumVitalsReporter />
                <DevPerfTracker />
                <AnonPresence />
                {!showTauriShell && <CookieConsent />}
                {!showTauriShell && <ThirdPartyAnalytics />}
              </AuthProvider>
            </TooltipProvider>
          </QueryProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </NextIntlClientProvider>
  );
}

export type RootContentProps = {
  children: React.ReactNode;
  locale: string;
  messages: Record<string, unknown>;
  timeZone: string;
};

export function RootContent({ children, locale, messages, timeZone }: RootContentProps) {
  // Для кода вне React (валидация, сервисы): те же сообщения, без второго словаря в бандле.
  setUiMessages(locale, messages as AbstractIntlMessages);
  const pathname = usePathname();
  if (pathname === "/overlay") {
    return <OverlayShell>{children}</OverlayShell>;
  }
  if (pathname === "/preloader") {
    return (
      <NextIntlClientProvider
        locale={locale}
        messages={messages}
        timeZone={timeZone}
        onError={intlOnError}
        getMessageFallback={intlMessageFallback}
      >
        <ThemeProvider
          attribute="class"
          defaultTheme="dark"
          disableTransitionOnChange
        >
          <UiScaleInit />
          {children}
        </ThemeProvider>
      </NextIntlClientProvider>
    );
  }
  return (
    <FullAppShell
      locale={locale}
      messages={messages}
      timeZone={timeZone}
      pathname={pathname}
    >
      {children}
    </FullAppShell>
  );
}
